import React, { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import AcreedoresApi from "api/acreedoresApi";

const CreateAcreedor = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    tipo_identificacion: "",
    identificacion: "",
    tipo: "PERSONA", // Por defecto para CC/CE
    nombre: "",
    apellido: "",
    direccion: "",
    ciudad: "",
    numero_contacto: "",
    es_recurrente: false, // Siempre no recurrente
    correos: [],
  });

  const [errors, setErrors] = useState({});
  const [acreedorExistente, setAcreedorExistente] = useState(false);

  const handleIdentificacionBlur = async () => {
    const identificacion = formData.identificacion;
    if (identificacion.length < 5) {
      setErrors((prev) => ({
        ...prev,
        identificacion: "Debe tener al menos 5 caracteres",
      }));
      return;
    }
    try {
      const response = await AcreedoresApi.fetchAcreedorByIdentificacion(identificacion);
      const acreedor = response.data;
      if (acreedor) {
        setAcreedorExistente(true);
        setErrors((prev) => ({
          ...prev,
          identificacion: "La identificación ya existe, debe ingresar una diferente",
        }));
      } else {
        setAcreedorExistente(false);
      }
    } catch (error) {
      console.error("Error al verificar acreedor:", error);
      setErrors((prev) => ({
        ...prev,
        identificacion: "Error al verificar la identificación",
      }));
    }
  };

  const handleIdentificacionChange = (e) => {
    const identificacion = e.target.value;
    // Aplicar formato según tipo de identificación
    const cleanValue =
      formData.tipo_identificacion === "NIT"
        ? identificacion.replace(/[^\d-]/g, "").slice(0, 20)
        : identificacion.replace(/\D/g, "").slice(0, 20);
    setFormData((prev) => ({ ...prev, identificacion: cleanValue }));
    setErrors((prev) => ({ ...prev, identificacion: "" }));
    setAcreedorExistente(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "nombre" || name === "apellido") {
      const cleanValue = value.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ\s]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else if (name === "tipo") {
      const cleanValue = value.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ\s]/g, "").toUpperCase();
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else if (name === "numero_contacto") {
      const cleanValue = value.replace(/\D/g, "").slice(0, 15);
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else if (name === "tipo_identificacion") {
      if (value === "NIT") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          tipo: "",
          apellido: "",
          identificacion: "",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          tipo: "PERSONA",
          identificacion: "",
        }));
      }
      setErrors((prev) => ({ ...prev, identificacion: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCorreoChange = (index, value) => {
    const updatedCorreos = [...formData.correos];
    updatedCorreos[index] = { ...updatedCorreos[index], email: value };
    setFormData((prev) => ({ ...prev, correos: updatedCorreos }));
  };

  const addCorreo = () => {
    setFormData((prev) => ({
      ...prev,
      correos: [...prev.correos, { email: "", orden: prev.correos.length }],
    }));
  };

  const removeCorreo = (index) => {
    const updatedCorreos = formData.correos.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      correos: updatedCorreos.map((correo, i) => ({ ...correo, orden: i })),
    }));
  };

  const moveCorreo = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === formData.correos.length - 1)
    ) {
      return;
    }

    const updatedCorreos = [...formData.correos];
    const temp = updatedCorreos[index];
    updatedCorreos[index] = updatedCorreos[index + direction];
    updatedCorreos[index + direction] = temp;

    setFormData((prev) => ({
      ...prev,
      correos: updatedCorreos.map((correo, i) => ({ ...correo, orden: i })),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.identificacion) {
      setErrors((prev) => ({
        ...prev,
        identificacion: "La identificación es un campo obligatorio.",
      }));
      return;
    }
    if (acreedorExistente) {
      onSuccess();
      onClose();
    } else {
      try {
        const dataToSend = {
          ...formData,
          apellido: formData.tipo_identificacion === "NIT" ? "" : formData.apellido,
          correos_data: formData.correos.map((correo, index) => ({
            email: correo.email,
            orden: index,
          })),
        };
        await AcreedoresApi.createAcreedor(dataToSend);
        onSuccess();
        onClose();
      } catch (error) {
        console.error("Error al guardar acreedor:", error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <SuiTypography variant="h4" fontWeight="dark">
          Nuevo Acreedor
        </SuiTypography>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Tipo de Identificación
              </SuiTypography>
              <Select
                name="tipo_identificacion"
                value={formData.tipo_identificacion}
                onChange={handleChange}
                label="Tipo de Identificación"
                disabled={acreedorExistente}
              >
                <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
                <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                <MenuItem value="PAS">Pasaporte</MenuItem>
              </Select>
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Identificación (Si no la conoce: 11111-X)
              </SuiTypography>
              <SuiInput
                name="identificacion"
                placeholder={
                  formData.tipo_identificacion === "NIT"
                    ? "123456789-1"
                    : formData.tipo_identificacion === "PAS"
                    ? "Alfanumérico 5-20 caracteres"
                    : "Ingrese máximo 20 dígitos numéricos"
                }
                value={formData.identificacion}
                onChange={handleIdentificacionChange}
                onBlur={handleIdentificacionBlur}
                error={Boolean(errors.identificacion)}
                fullWidth
                disabled={false}
              />
              {errors.identificacion && (
                <SuiTypography variant="caption" color="error">
                  {errors.identificacion}
                </SuiTypography>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Nombres
              </SuiTypography>
              <SuiInput
                name="nombre"
                placeholder={formData.tipo_identificacion === "NIT" ? "Razón Social" : "Nombre"}
                value={formData.nombre}
                onChange={handleChange}
                fullWidth
                disabled={acreedorExistente}
              />
            </Grid>
            {formData.tipo_identificacion !== "NIT" && (
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Apellidos
                </SuiTypography>
                <SuiInput
                  name="apellido"
                  placeholder="Apellido"
                  value={formData.apellido || ""}
                  onChange={handleChange}
                  fullWidth
                  disabled={acreedorExistente}
                />
              </Grid>
            )}
            {formData.tipo_identificacion === "NIT" && (
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Tipo
                </SuiTypography>
                <SuiInput
                  name="tipo"
                  placeholder="Tipo"
                  value={formData.tipo || ""}
                  onChange={handleChange}
                  helperText="Solo letras y espacios permitidos (se convertirá a mayúsculas)"
                  fullWidth
                  disabled={acreedorExistente}
                />
              </Grid>
            )}
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Dirección
              </SuiTypography>
              <SuiInput
                name="direccion"
                placeholder="Dirección"
                value={formData.direccion || ""}
                onChange={handleChange}
                multiline
                disabled={acreedorExistente}
                rows={2}
                fullWidth
              />
            </Grid>
            <Grid size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Ciudad
              </SuiTypography>
              <SuiInput
                name="ciudad"
                value={formData.ciudad || ""}
                onChange={handleChange}
                fullWidth
                disabled={acreedorExistente}
              />
            </Grid>
            <Grid size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                No. Contacto
              </SuiTypography>
              <SuiInput
                name="numero_contacto"
                placeholder="Número de Contacto"
                value={formData.numero_contacto || ""}
                onChange={handleChange}
                fullWidth
                disabled={acreedorExistente}
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                ¿Es Recurrente?{" "}
                <Switch
                  checked={formData.es_recurrente}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, es_recurrente: e.target.checked }))
                  }
                  name="es_recurrente"
                  disabled={acreedorExistente}
                />
              </SuiTypography>
            </Grid>
          </Grid>
          {/* Sección de Correos */}
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SuiBox component="h6">Correos Electrónicos</SuiBox>
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  size="small"
                  onClick={addCorreo}
                  disabled={acreedorExistente}
                >
                  <AddIcon /> Agregar Correo
                </SuiButton>
              </SuiBox>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {formData.correos.map((correo, index) => (
                <SuiBox key={index} display="flex" alignItems="center" mb={2}>
                  <SuiInput
                    value={correo.email}
                    onChange={(e) => handleCorreoChange(index, e.target.value)}
                    placeholder="Correo electrónico"
                    fullWidth
                  />
                  <IconButton onClick={() => moveCorreo(index, -1)} disabled={index === 0}>
                    <ArrowUpward />
                  </IconButton>
                  <IconButton
                    onClick={() => moveCorreo(index, 1)}
                    disabled={index === formData.correos.length - 1}
                  >
                    <ArrowDownward />
                  </IconButton>
                  <IconButton onClick={() => removeCorreo(index)} color="errors">
                    <DeleteIcon />
                  </IconButton>
                </SuiBox>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={onClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            Crear
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

CreateAcreedor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default CreateAcreedor;
